import React from "react";
import { Briefcase, Users, Award, MapPin, TrendingUp, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "../lib/useSEO";

const PILLARS = [
    {
        icon: Briefcase,
        title: "Hands on operations background",
        body: "More than a decade leading hospitality operations from the front line through to multi property leadership across hotels, serviced apartments, restaurants and bars, covering front of house, housekeeping, food and beverage, guest services and back of house.",
    },
    {
        icon: Users,
        title: "Team leadership and development",
        body: "Built, mentored and turned around operational teams across the hospitality sector. Good service is engineered through good people and clear standards, regardless of format or scale.",
    },
    {
        icon: TrendingUp,
        title: "Commercial performance",
        body: "Worked alongside owners, operators and asset managers to lift commercial outcomes through revenue strategy, pricing, cost discipline and the operational levers that move profitability and contribution.",
    },
    {
        icon: ClipboardCheck,
        title: "Standards, SOPs and quality",
        body: "Designed and rolled out SOPs and quality assurance programmes that scale across hotels, serviced apartments and restaurant or bar groups without losing the human craft of hospitality.",
    },
];

export default function AboutPage() {
    useSEO({
        title: "About — Optimum Hospitality Solutions",
        description:
            "Meet the founder of Optimum Hospitality Solutions, a London-based hospitality operations leader with over 10 years of experience across hotels, serviced apartments, restaurants, bars and the wider hospitality sector.",
        path: "/about",
    });

    return (
        <div data-testid="about-page">
            {/* HERO */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            About Sidney Adams
                        </span>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                            A practitioner&rsquo;s<br />
                            <em className="italic text-brand-champagne not-italic font-display">approach</em> to consulting.
                        </h1>
                        <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                            Optimum Hospitality Solutions is led by Sidney Adams, a hospitality operations leader with over a decade of experience across hotels, serviced apartments, restaurants and bars. Every recommendation we make has been tested in a real operation, not in a slide deck.
                        </p>
                        <div className="mt-8 flex items-center gap-3 text-brand-ink/70 text-sm">
                            <MapPin size={16} className="text-brand-champagne" />
                            Based in London. Working across the UK
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <img
                            src="https://images.unsplash.com/photo-1740933084056-078fac872bff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBMb25kb258ZW58MHx8fHwxNzgxMTIyMzg1fDA&ixlib=rb-4.1.0&q=85"
                            alt="Professional consulting"
                            className="w-full h-[420px] sm:h-[520px] object-cover"
                            width="800"
                            height="520"
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>

            {/* STORY */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Background
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl mt-5 leading-tight text-brand-navy">
                            Over a decade on property.
                        </h2>
                    </div>
                    <div className="lg:col-span-8 space-y-6 text-brand-ink/80 leading-relaxed">
                        <p>
                            My career in hospitality has been spent largely in the property: in lobbies, in operations meetings, in housekeeping departments and in finance reviews. I have led teams through openings, repositions, brand transitions and difficult trading conditions. That experience on the ground shapes how I consult today.
                        </p>
                        <p>
                            I have worked across hotels, serviced apartments and guest accommodation, with exposure to both branded and independent environments. Multi-property responsibility taught me that great operations look different at different scales. What works in a 30-key boutique will not necessarily fit a 200-unit aparthotel portfolio.
                        </p>
                        <p>
                            That blend of operational, commercial and people leadership is what Optimum Hospitality Solutions brings to every engagement: a sharp focus on the operational reality of running a property, with the commercial discipline that owners and investors expect.
                        </p>
                    </div>
                </div>
            </section>

            {/* HOSPITALITY CAREER JOURNEY */}
            <section className="border-b border-brand-border" data-testid="career-timeline">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Hospitality Career Journey
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight text-brand-navy">
                            A career built on operations.
                        </h2>
                        <p className="mt-6 text-brand-ink/75 leading-relaxed">
                            From early operations roles through to multi site leadership, and then a focused consulting practice. The same thread runs through it: working in the property, alongside the team, to lift the operation.
                        </p>
                    </div>

                    <ol className="mt-14 relative">
                        <span aria-hidden="true" className="absolute left-3 sm:left-4 top-2 bottom-2 w-px bg-brand-border" />
                        {[
                            {
                                phase: "01",
                                title: "Early hospitality operations experience",
                                body: "Front line operational roles across rooms division, food and beverage and guest services. Learning what good hospitality looks like, shift by shift, from the people running it.",
                            },
                            {
                                phase: "02",
                                title: "Leadership and management responsibilities",
                                body: "Heading up departments and then full properties. Running operations meetings, owning the P&L, recruiting and developing teams, and standing accountable for the guest experience the operation produced.",
                            },
                            {
                                phase: "03",
                                title: "Operational improvement and business performance focus",
                                body: "Working across multi site environments and brand transitions, focused on the operating layer of the business: labour productivity, standards, guest satisfaction and the operational levers that move GOP.",
                            },
                            {
                                phase: "04",
                                title: "Establishment of Optimum Hospitality Solutions",
                                body: "Optimum Hospitality Solutions Ltd was established to bring that operational experience directly to hotel owners, general managers and operations directors who want practical work in the property, not theoretical advice.",
                            },
                        ].map((step, idx) => (
                            <li
                                key={step.phase}
                                data-testid={`timeline-step-${idx}`}
                                className="relative pl-12 sm:pl-16 pb-10 last:pb-0"
                            >
                                <span
                                    aria-hidden="true"
                                    className="absolute left-0 top-1 flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center bg-brand-stone border border-brand-champagne text-[10px] sm:text-xs font-display text-brand-champagne"
                                >
                                    {step.phase}
                                </span>
                                <h3 className="font-display text-xl sm:text-2xl text-brand-navy leading-snug">
                                    {step.title}
                                </h3>
                                <p className="mt-3 text-brand-ink/75 leading-relaxed max-w-3xl">
                                    {step.body}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* PILLARS */}
            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            What I bring
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight text-brand-navy">
                            Four pillars of practice.
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-border">
                        {PILLARS.map((p, idx) => (
                            <div
                                key={p.title}
                                data-testid={`about-pillar-${idx}`}
                                className="bg-brand-stone p-10 hover:bg-white transition-colors"
                            >
                                <p.icon className="text-brand-champagne" size={28} strokeWidth={1.25} />
                                <h3 className="font-display text-xl mt-5 text-brand-navy">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-sm text-brand-ink/75 leading-relaxed">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* VALUES */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            How I work
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl mt-5 leading-tight text-brand-navy">
                            Clear, practical, accountable.
                        </h2>
                        <p className="mt-6 text-brand-ink/75 leading-relaxed">
                            Engagements are scoped tightly, scoped honestly and delivered in the property where it matters. No oversized teams. No 100 page reports that nobody reads.
                        </p>
                    </div>
                    <div className="lg:col-span-7 space-y-8">
                        {[
                            { n: "01", t: "Listen first", b: "Every engagement starts with understanding the business, the team and the owner's objectives, not the playbook." },
                            { n: "02", t: "Diagnose honestly", b: "Findings are direct, grounded in evidence and prioritised by impact. We focus on the levers that actually move performance." },
                            { n: "03", t: "Recommend pragmatically", b: "Recommendations have to be deliverable by the team you have, not the team a consultant imagines you have." },
                            { n: "04", t: "Stay involved", b: "Implementation matters more than the report. We stay close through delivery so change actually holds." },
                        ].map((v) => (
                            <div key={v.n} className="grid grid-cols-12 gap-6 items-start border-b border-brand-border pb-7 last:border-0">
                                <div className="col-span-2 lg:col-span-1 text-brand-champagne font-display text-2xl">{v.n}</div>
                                <div className="col-span-10 lg:col-span-11">
                                    <h3 className="font-display text-xl text-brand-navy">{v.t}</h3>
                                    <p className="mt-2 text-brand-ink/75 leading-relaxed text-sm">{v.b}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-brand-navy">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Work together
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight text-brand-stone">
                            Have a hospitality business in mind? Let&rsquo;s talk.
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:text-right flex flex-wrap lg:justify-end gap-4">
                        <Link
                            to="/booking"
                            data-testid="about-cta-booking"
                            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white transition-colors"
                        >
                            Book a consultation
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="about-cta-contact"
                            className="inline-flex items-center gap-2 px-7 py-3.5 border border-brand-champagne text-brand-stone text-sm tracking-wide hover:bg-brand-champagne hover:text-brand-navy transition-colors"
                        >
                            Get in touch
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
